/**
 * <bkg-pack-picker> — the quantity-tier + purchase-option picker for the
 * Betakaroten Gold PDP buy box. Markup, CSS and every price string come from
 * blocks/bkg-pack-picker.liquid. Spec: docs/multipack-target-shape.md.
 *
 * Ported from docs/reference/hair-luxious/hl-pack-picker.js (snapshot
 * 2026-09-09). The divergences are listed at the bottom of this comment.
 *
 * WHAT THIS SCRIPT DOES NOT DO: money arithmetic. Every (quantity x purchase
 * option) price is precomputed server-side with Shopify's `money` filter and
 * shipped in a <script type="application/json"> blob. This element looks up
 * prepared strings and writes them into the DOM. That keeps the buy box correct
 * in EUR and CHF at once without a code change, and sidesteps the float class
 * of bug entirely — an exact 10% saving computed in JS lands on
 * 0.09999999999999993 and displays as "9%".
 *
 * WHAT IT WRITES TO THE CART FORM
 *   quantity      -> the selected pack size, into Horizon's real
 *                    input[name="quantity"] when the nested `quantity` block is
 *                    enabled, and otherwise into the block's own hidden
 *                    fallback. Never both.
 *   selling_plan  -> the chosen plan id, or "" for a one-time purchase.
 *
 * The pack DISCOUNT is not written anywhere. On a one-time line it is applied
 * at the cart by Bundler's discount function from the line quantity; on a
 * subscription line it is already inside the plan's pricing policy. This
 * element's only job on that front is to make sure the quantity and the plan
 * that reach the cart are the ones the shopper was quoted.
 *
 * DIVERGENCES FROM THE REFERENCE
 *   1. Two ladders. Each tier declares which purchase options offer it, so the
 *      visible card set, the top tier, the stepper floor and the "N+" numeral
 *      are all resolved per mode rather than once.
 *   2. Changing mode can invalidate the selected quantity. #snap() moves to the
 *      nearest rung the new mode offers, downwards on a tie, so the shopper is
 *      never silently moved to a larger spend and never sees an empty ladder.
 *   3. The frequency row stays visible as static text under the coupled
 *      arrangement instead of hiding itself. Pack size implying cadence is not
 *      obvious, and it is the one place the shopper reads it before the recap.
 *   4. No free-delivery ribbon or note; shipping has no quantity threshold in
 *      any zone this store sells to.
 *   5. Keeps Horizon's sticky add-to-cart bar in step directly. It renders the
 *      variant's own price, which under a quantity model is the 1-pack price
 *      whatever the shopper picked.
 *
 * UPSTREAM risk: MEDIUM. Depends on the buy-buttons form id
 * (`BuyButtons-ProductForm-<section.id>`) and, when Horizon's nested quantity
 * block is enabled, on an input[name="quantity"] inside it. Both are checked at
 * runtime with a fallback, so a Horizon change degrades to a working
 * add-to-cart rather than a wrong one.
 */

class BkgPackPicker extends HTMLElement {
  connectedCallback() {
    if (this.dataset.mounted === 'true') return;
    this.dataset.mounted = 'true';

    this.prices = this.#parseJson('[data-pack-prices]', []);
    this.plans = this.#parseJson('[data-pack-plans]', []);
    this.qtyLabels = this.#parseJson('[data-qty-labels]', {});

    this.otAvailable = this.dataset.otAvailable === 'true';
    this.subAvailable = this.dataset.subAvailable === 'true';
    this.otTop = parseInt(this.dataset.otTop, 10) || 0;
    this.subTop = parseInt(this.dataset.subTop, 10) || 0;
    // maxQty exists because every price is prepared server-side: the stepper
    // can only reach quantities that have a prepared row, which is what keeps
    // money arithmetic out of this file.
    this.maxQty = parseInt(this.dataset.maxQty, 10) || 1;
    this.unitCents = parseInt(this.dataset.unitCents, 10) || 0;
    this.topTierIsMinimum = this.dataset.topTierIsMinimum === 'true';
    // True once any tier declares its own selling plan ids. From that point a
    // tier WITHOUT a plan set is not a subscription option, however its
    // checkbox is ticked — see #cardInMode.
    this.tierPlans = this.dataset.tierPlans === 'true';
    // tier_display: table. The stepper is the whole quantity control from 1
    // up, the card row stays hidden whatever the tier count, and the tier table
    // under the stepper follows the quantity. See snippets/bkg-pack-tiers.
    this.tableMode = this.dataset.tierDisplay === 'table';
    this.tierRows = Array.from(this.querySelectorAll('[data-tier-row]'));

    this.cards = Array.from(this.querySelectorAll('[data-qty-card]'));

    // planSet BEFORE planId, and both before anything reads state. In the
    // reference this was one object literal calling #firstPlanId(), which set
    // planSet as a side effect onto a temporary object that the assignment then
    // replaced — so planSet was undefined on mount and the page loaded showing
    // the wrong tier's recurring rate until the shopper touched something.
    // Order of assignment, not logic, was the whole bug.
    this.state = { qty: 1, mode: 'onetime', planSet: '', planId: '' };
    this.#adoptDomSelection();

    // `change`, not `click` on the label. When a <label> wraps a visually
    // hidden radio, the browser's label-to-control forwarding happens as part
    // of the same click event and its ordering relative to listeners added here
    // is not guaranteed — a click handler can still read the PREVIOUS :checked
    // state and write the wrong quantity to the form while the card highlights
    // correctly. `change` fires after the control has updated, so the race
    // cannot occur, and it covers keyboard arrow-key navigation for free.
    this.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      if (target.matches('[data-qty-input]')) {
        this.state.qty = parseInt(target.value, 10) || 1;
        this.#adoptTierPlans(target.closest('[data-qty-card]'));
      } else if (target.matches('[data-plan-input]')) {
        this.state.mode = target.value;
        // The rung the shopper is standing on may not exist in the mode they
        // just switched to. Snap before anything reads the quantity.
        this.state.qty = this.#snap(this.state.qty, this.state.mode);
        this.#syncCardVisibility();
        this.#adoptTierPlans(this.#cardForQty(this.state.qty));
      } else if (target.matches('[data-freq-select]')) {
        this.state.planId = target.value;
      } else {
        return;
      }
      this.#apply(true);
    });

    // Stepper: moves within the open-ended top tier only, and that tier's floor
    // depends on the purchase option (3 buying once, 4 subscribing). Clamped to
    // a quantity that has a prepared price.
    this.addEventListener('click', (event) => {
      const step = event.target.closest('[data-qty-decrease], [data-qty-increase]');
      if (!step) return;
      const delta = step.hasAttribute('data-qty-increase') ? 1 : -1;
      const floor = this.#stepperFloor(this.state.mode);
      const next = Math.min(this.maxQty, Math.max(floor, this.state.qty + delta));
      if (next === this.state.qty) return;
      this.state.qty = next;
      this.#apply(true);
    });

    this.#apply(true);

    // BACK-NAVIGATION. Browsers restore the checked state of radios when the
    // shopper returns to a page, and they do it without telling anyone: no
    // change event fires, and on a bfcache restore connectedCallback does not
    // run again either. The radios then say "2 bottles, One-time" while this
    // element still believes its server-rendered defaults, and the summary
    // underneath describes an order nobody chose — reported 2026-09-14 as a
    // subscription recap sitting under a one-time selection.
    //
    // `pageshow` fires on both kinds of restore, and re-reading the DOM is a
    // no-op on a normal load, so it is unconditional. Re-reading rather than
    // resetting is deliberate: the shopper's restored choice is the one they
    // made, and throwing it away to force agreement would be the same bug with
    // better-looking symptoms.
    window.addEventListener('pageshow', this.#onPageShow);

    // Seal Subscriptions keeps running its own init after DOM ready even with
    // its widget hidden via display:none, injecting and blanking its own
    // `selling_plan` input. A single write on load is therefore not enough: it
    // is correct at the time, and then Seal's late-arriving input appears after
    // it. That is the "works only if I click something first" bug — any click
    // re-ran the write after Seal had finished, a fresh page load never did.
    //
    // So the write is not a one-shot, it is an invariant re-asserted whenever
    // the form's controls change. A setTimeout guess at Seal's init timing was
    // tried on a sibling build and is unreliable on slow connections; an
    // observer needs no guess, because it reacts to the actual event.
    this.#observer = new MutationObserver(() => this.#writeForm());
    const form = this.#form();
    if (form) {
      this.#observer.observe(form, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['value', 'name', 'disabled']
      });
    }

    // Seal's own signal that a widget now exists. This is what the observer
    // above approximates, and the proper hook to re-apply our state onto a
    // freshly created widget. It may only fire during Seal's initial page-load
    // pass, which a script running after load cannot observe, so it is wired as
    // a bonus rather than relied on. The observer stays.
    document.addEventListener('sealsubs:subscription_widget_created', this.#onSealWidgetCreated);

    // Fires when Seal's own widget changes the plan. Our UI is the source of
    // truth, so a divergence means something else moved the selection and we
    // re-assert. Guarded by #writing so our own dispatch cannot loop back.
    document.addEventListener('sealsubs:selling_plan_changed', this.#onSealPlanChanged);

    // Belt and braces: the capture phase of `submit` runs before the form's own
    // target-phase handling, so this has the last word no matter what. Kept
    // because the sticky bar's button is driven by a click handler rather than
    // a real submit, and accelerated-checkout buttons read the form directly
    // without ever firing one.
    document.addEventListener('submit', this.#onSubmitCapture, true);
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.#onPageShow);
    document.removeEventListener('submit', this.#onSubmitCapture, true);
    document.removeEventListener('sealsubs:subscription_widget_created', this.#onSealWidgetCreated);
    document.removeEventListener('sealsubs:selling_plan_changed', this.#onSealPlanChanged);
    this.#observer?.disconnect();
  }

  /** @type {MutationObserver | undefined} */
  #observer;

  /** Guards against the observer reacting to its own writes. */
  #writing = false;

  /** True while a deferred re-write is already scheduled. At most one ever is. */
  #writeQueued = false;

  /** Does the selected pack size offer a cadence CHOICE, not just a cadence? */
  #freqChoosable = false;

  #onPageShow = () => {
    this.#adoptDomSelection();
    this.#apply(true);
  };

  #onSealWidgetCreated = () => this.#writeForm();

  #onSealPlanChanged = (event) => {
    if (this.#writing) return;
    const reported = String(event?.detail?.selling_plan_id ?? '');
    if (reported === this.#currentPlanKey()) return;
    this.#writeForm();
  };

  #onSubmitCapture = (event) => {
    if (event.target === this.#form()) this.#writeForm();
  };

  #parseJson(selector, fallback) {
    const node = this.querySelector(selector);
    try {
      return JSON.parse(node?.textContent || 'null') ?? fallback;
    } catch (error) {
      // A malformed blob must not take the add-to-cart button down with it.
      console.warn(`[bkg-pack-picker] could not parse ${selector}:`, error);
      return fallback;
    }
  }

  // --- mode-aware tier resolution ------------------------------------------

  /**
   * Whether a tier card belongs to a purchase option.
   *
   * The subscription arm carries an extra condition that the one-time arm does
   * not: the tier's plan has to actually be on the product.
   *
   * Two ways it can fail to be. The tier names no plan ids at all, or it names
   * ids that are not among the variant's allocations — which is the normal
   * state before the Seal rules are repointed at the base variant, and was
   * visible in the theme editor on 2026-09-14 as a 2-bottle and a 4-bottle card
   * with no discount badge while only the 1-month plan was attached.
   *
   * Either way there is no plan to send, and #syncFrequencies returns early
   * rather than clearing the selection, so the tier would inherit whatever plan
   * was last chosen. A shopper picking four bottles would be signed up at the
   * one-bottle rate, forever, and nothing downstream re-checks the pairing:
   * Shopify accepts the line and bills it. Hiding the card is the only safe
   * reading of "offered on subscription, no plan available", and it is also the
   * honest one — that pack size genuinely is not purchasable as a subscription
   * until its rule points at this variant.
   *
   * docs/validate-pack-picker.py fails the push on the empty-setting half of
   * this. The unattached-plan half can only be seen at render time, which is
   * why the check lives here as well.
   */
  #cardInMode(card, mode) {
    if (mode !== 'subscribe') return card.dataset.ot === '1';
    if (card.dataset.sub !== '1') return false;
    if (!this.tierPlans) return true;
    const set = card.dataset.plansForQty;
    return Boolean(set) && this.plans.some((plan) => set.includes(`,${plan.id},`));
  }

  /** The top tier of a mode. Not one number: one-time tops at 3, subscribe at 4. */
  #top(mode) {
    return mode === 'subscribe' ? this.subTop : this.otTop;
  }

  // Where the stepper bottoms out. In card mode it only moves inside the
  // open-ended top tier; in table mode it is the only control, so it starts
  // at one.
  #stepperFloor(mode) {
    return this.tableMode ? 1 : this.#top(mode);
  }

  /**
   * Every quantity the given mode can reach, ascending. The tier quantities it
   * offers, plus — when the top tier is a minimum — every quantity above that
   * tier up to the highest one with a prepared price.
   */
  #validQtys(mode) {
    const qtys = this.cards
      .filter((card) => this.#cardInMode(card, mode))
      .map((card) => parseInt(card.dataset.qty, 10))
      .filter((qty) => qty > 0);
    if (this.topTierIsMinimum) {
      for (let q = this.#top(mode) + 1; q <= this.maxQty; q += 1) qtys.push(q);
    }
    return Array.from(new Set(qtys)).sort((a, b) => a - b);
  }

  /**
   * Nearest rung the mode actually offers.
   *
   * A shopper on 3 bottles who switches to Subscribe has picked a rung that
   * does not exist there — tier 3 has no plan. Rendering an empty ladder or
   * leaving an unselectable quantity in the form are both worse than moving
   * them. Ties break DOWNWARDS (3 becomes 2, not 4), because a snap that
   * silently increases what the shopper is about to pay is the one direction
   * that is not recoverable by them noticing.
   */
  #snap(qty, mode) {
    const valid = this.#validQtys(mode);
    if (!valid.length || valid.includes(qty)) return qty;
    let best = valid[0];
    let bestDistance = Infinity;
    for (const candidate of valid) {
      const distance = Math.abs(candidate - qty);
      // Strictly less-than, over an ascending list, means an equal-distance
      // candidate above never displaces the one below.
      if (distance < bestDistance) {
        best = candidate;
        bestDistance = distance;
      }
    }
    return best;
  }

  /** The tier card that owns a quantity: an exact match, else the top tier. */
  #cardForQty(qty) {
    const inMode = this.cards.filter((card) => this.#cardInMode(card, this.state.mode));
    return (
      inMode.find((card) => parseInt(card.dataset.qty, 10) === qty) ||
      inMode.find((card) => parseInt(card.dataset.qty, 10) === this.#top(this.state.mode)) ||
      null
    );
  }

  /**
   * Take the selection from the radios, not from the server-rendered defaults.
   *
   * The radios are the only place the shopper's choice survives a back
   * navigation, so on every entry to the page they are the source of truth and
   * the `data-default-*` attributes are the fallback for a first visit. The
   * restored quantity is snapped against the restored mode before anything
   * reads it, because a browser will happily restore "3 bottles" next to
   * "Subscribe" — a pair that was never selectable.
   *
   * Everything downstream (card visibility, the checked card, the tier's plan,
   * the frequency list) is then derived, so this is the one place that decides
   * what the picker is showing.
   */
  #adoptDomSelection() {
    const restoredMode = this.querySelector('[data-plan-input]:checked')?.value;
    const wanted = restoredMode || this.dataset.defaultMode;
    this.state.mode = wanted === 'subscribe' && this.subAvailable ? 'subscribe' : 'onetime';

    const restoredQty = parseInt(this.querySelector('[data-qty-input]:checked')?.value, 10);
    const fallbackQty = parseInt(this.dataset.defaultQty, 10) || 1;
    this.state.qty = this.#snap(Number.isFinite(restoredQty) ? restoredQty : fallbackQty, this.state.mode);

    this.#syncCardVisibility();
    this.#adoptTierPlans(this.#cardForQty(this.state.qty));
    if (!this.state.planId) this.state.planId = this.#firstPlanId();
  }

  #adoptTierPlans(card) {
    if (!card) return;
    // Quantity-scoped rules: the tier carries its own selling plan, so changing
    // pack size changes which plan is sent. This is the only thing pairing the
    // two — Seal cannot scope a plan to a quantity, and nothing downstream
    // re-checks it.
    this.state.planSet = card.dataset.plansForQty || '';
    this.#syncFrequencies();
  }

  /**
   * Shows the cards the current mode offers, hides the rest, and keeps the
   * checked radio on a card that is actually visible.
   */
  #syncCardVisibility() {
    const mode = this.state.mode;
    const top = this.#top(mode);
    for (const card of this.cards) {
      const visible = this.#cardInMode(card, mode);
      card.hidden = !visible;
      const input = card.querySelector('[data-qty-input]');
      if (input) input.disabled = !visible;

      // "3+" buying once, "4+" subscribing: the numeral has to follow the mode,
      // not a fixed last tier.
      const numeral = card.querySelector('[data-qty-numeral]');
      if (numeral) {
        const base = numeral.dataset.numeral;
        numeral.textContent = this.topTierIsMinimum && parseInt(base, 10) === top ? `${base}+` : base;
      }
    }

    const target = this.#cardForQty(this.state.qty);
    const input = target?.querySelector('[data-qty-input]');
    if (input && !input.checked) input.checked = true;

    // One tier in this mode is not a choice. The row and its label go, and the
    // stepper, whose floor is then that single tier, is the whole control.
    // Re-decided on every mode change, because the two ladders can differ.
    const single = this.tableMode || this.cards.filter((card) => !card.hidden).length <= 1;
    const cardsRow = this.querySelector('[data-qty-cards]');
    if (cardsRow) cardsRow.hidden = single;
    const label = this.querySelector('[data-qty-label]');
    if (label) label.hidden = single;
  }

  /**
   * Rebuilds the delivery-frequency list for the currently selected pack size.
   *
   * Each pack size maps to its own Seal rule, and a rule can hold several
   * frequencies. So the options are whatever plans belong to THIS tier's rule,
   * not every plan on the product — which would let a shopper pick the 2-pack
   * rule's cadence while buying 4.
   *
   * Matched on plan ids, never the group: Liquid's selling_plan_group_id is a
   * hash of the group's shape and structurally identical rules collapse to one
   * value, and plan names repeat across rules. The ids are the only
   * discriminator. Membership is tested comma-wrapped so 123 cannot match
   * inside 1234.
   */
  #syncFrequencies() {
    const set = this.state.planSet;
    const available = set ? this.plans.filter((plan) => set.includes(`,${plan.id},`)) : this.plans;
    if (!available.length) {
      this.#freqChoosable = false;
      return;
    }

    const previousName = this.#planName();
    const match = available.find((plan) => plan.name === previousName) || available[0];
    this.state.planId = match.id;

    // Both controls are in the DOM; this picks the one that fits the tier the
    // shopper is on. A tier with one cadence gets a line of text, a tier with
    // several gets the select. Deciding that server-side from the default tier
    // locked every other tier to whichever plan was adopted first.
    const choosable = available.length > 1;
    this.#freqChoosable = choosable;

    // Hidden here as well as in #apply, rather than relying on which of the two
    // runs last. Both have everything they need and they agree, so the row is
    // never briefly wrong in whichever order the events happen to arrive.
    const row = this.querySelector('[data-freq-row]');
    if (row) row.hidden = this.state.mode !== 'subscribe' || !choosable;

    const select = this.querySelector('[data-freq-select]');
    if (select) {
      select.innerHTML = available
        .map(
          (plan) =>
            `<option value="${plan.id}"${plan.id === match.id ? ' selected' : ''}>${escapeHtml(plan.name)}</option>`
        )
        .join('');
      select.hidden = !choosable;
    }

    const staticEl = this.querySelector('[data-freq-static]');
    if (staticEl) {
      staticEl.textContent = match.name;
      staticEl.dataset.sellingPlanId = match.id;
      staticEl.hidden = choosable;
    }
  }

  #firstPlanId() {
    const set = this.state?.planSet;
    if (set) {
      const first = this.plans.find((plan) => set.includes(`,${plan.id},`));
      if (first) return first.id;
    }
    const select = this.querySelector('[data-freq-select]');
    if (select && !select.hidden) return select.value;
    return this.querySelector('[data-freq-static]')?.dataset.sellingPlanId || '';
  }

  #form() {
    // Prefer the deterministic id Horizon's buy-buttons block gives this exact
    // form over a looser action*="/cart/add" match, so a quick-add modal
    // elsewhere in the DOM can never be mistaken for the main product form.
    return (
      document.getElementById(this.dataset.formId) ||
      document.querySelector(`#shopify-section-${this.dataset.sectionId} form[action*="/cart/add"]`)
    );
  }

  /** Look up a prepared price row. Returns null when the combination is absent. */
  /**
   * Tier steps (table mode only). The step that applies to the selected
   * quantity is the highest tier at or below it. The nudge line under the
   * steps, in order of preference:
   *   - top tier reached: "Bester Preis. Du sparst X." (%SAVE%)
   *   - the next tier costs no more than the current quantity (15/25/50 makes
   *     2 and 3 cost the same): "3 Dosen zum Preis von 2" (%N%, %C%)
   *   - otherwise point at the top tier: "Ab 3 Dosen: -50% auf jede Dose"
   * Totals are compared from the prepared rows (via #row, so Bundler's
   * half-cent rounding is already resolved); both strings come from the same
   * money format, so their digits compare as cents.
   */
  #renderTierTable(qty) {
    if (!this.tableMode || this.tierRows.length === 0) return;
    const cents = (s) => parseInt(String(s || '').replace(/[^0-9]/g, ''), 10) || 0;
    let active = null;
    let nextQty = 0;
    let topQty = 0;
    let topPct = 0;
    for (const tierRow of this.tierRows) {
      const tierQty = parseInt(tierRow.dataset.tierQty, 10) || 1;
      if (tierQty <= qty) active = tierRow;
      else if (!nextQty) nextQty = tierQty;
      if (tierQty > topQty) { topQty = tierQty; topPct = parseInt(tierRow.dataset.tierPct, 10) || 0; }
    }
    for (const tierRow of this.tierRows) tierRow.toggleAttribute('data-active', tierRow === active);
    const hint = this.querySelector('[data-tier-hint]');
    const table = this.querySelector('[data-tier-table]');
    if (!hint || !table) return;
    const current = this.#row(qty, '');
    if (qty >= topQty) {
      hint.textContent = (table.dataset.bestHint || '').replace('%SAVE%', current ? current.save : '');
      return;
    }
    const nextRow = nextQty ? this.#row(nextQty, '') : null;
    if (current && nextRow && cents(nextRow.total) <= cents(current.total)) {
      hint.textContent = (table.dataset.nudgeFree || '').replace('%N%', String(nextQty)).replace('%C%', String(qty));
    } else {
      hint.textContent = (table.dataset.nudgeTop || '').replace('%Q%', String(topQty)).replace('%PCT%', `−${topPct}%`);
    }
  }


  #row(qty, plan) {
    const row = this.prices.find((entry) => entry.qty === qty && entry.plan === plan) || null;
    if (!row || plan !== '' || !row.alt || !this.unitCents) return row;

    // One-time rows whose discount lands on an exact half cent carry two
    // candidates. Bundler computes in floating point, in currency units, and
    // its float error decides which way the half goes (see the block header:
    // fitted to 54 cart probes). Reproduce that one computation and take the
    // candidate it lands on. This picks between two strings Liquid formatted;
    // it never produces a price of its own.
    const disc = Math.round(((this.unitCents / 100) * qty) * (row.pct / 100) * 100);
    if (disc !== row.alt.disc) return row;
    return { ...row, total: row.alt.total, unit: row.alt.unit, save: row.alt.save };
  }

  #currentPlanKey() {
    return this.state.mode === 'subscribe' ? this.state.planId : '';
  }

  #planName() {
    const select = this.querySelector('[data-freq-select]');
    if (select && !select.hidden) return select.options[select.selectedIndex]?.textContent.trim() || '';
    return this.querySelector('[data-freq-static]')?.textContent.trim() || '';
  }

  /**
   * The cadence as a phrase rather than a bare plan name. Plan names are
   * cadences in their own right ("1 month"), which reads fine in a select next
   * to "Deliver every" but not dropped into a sentence — "Then EUR 50,98 2
   * months" is not a sentence. Prefixing turns every name into a phrase that
   * works in both places, without parsing merchant-authored text.
   */
  #cadencePhrase() {
    const name = this.#planName();
    if (!name) return '';
    const every = this.dataset.lEvery;
    return every ? `${every} ${name}` : name;
  }

  #qtyLabel(qty) {
    return (
      this.qtyLabels[String(qty)] ||
      `${qty} ${qty === 1 ? this.dataset.unitSingular : this.dataset.unitPlural}`
    );
  }

  #apply(writeToForm) {
    const qty = this.state.qty;
    const subscribing = this.state.mode === 'subscribe';
    const oneTime = this.#row(qty, '');
    const subscribe = this.subAvailable ? this.#row(qty, this.state.planId) : null;
    const multi = qty > 1;

    // --- purchase option cards -----------------------------------------------
    // When an introductory offer is configured, the BIG number is what the
    // shopper pays today and the small line states what recurs. Showing only the
    // intro price is the textbook subscription dark pattern, and the renewal
    // figure is required pre-purchase anyway — so it is never more than one line
    // away from the intro.
    if (subscribe) {
      const intro = subscribe.hasIntro;
      this.#text('[data-plan-unit]', subscribe.unit);
      this.#secondary(
        '[data-plan-total]',
        intro ? `${this.dataset.lThen} ${subscribe.recurUnit} / ${this.dataset.perLabel}` : subscribe.total,
        intro || multi
      );

      const badge = this.querySelector('[data-plan-extra]');
      if (badge) {
        // Percentage, not currency: comparable across products and pack sizes,
        // where "Save 76,50 EUR" only means something once you know the base.
        badge.textContent = subscribe.pct > 0 ? `−${subscribe.pct}%` : '';
        badge.hidden = subscribe.pct <= 0;
      }
    }

    if (oneTime) {
      this.#text('[data-onetime-unit]', oneTime.unit);

      // Single-option layout only; absent from the two-card layout, where these
      // lookups simply find nothing. Badge and strikethrough appear together
      // or not at all, so the page never shows a struck price without saying
      // what the saving is, or a percentage with nothing to measure it against.
      const discounted = oneTime.pct > 0;
      const offerBadge = this.querySelector('[data-onetime-badge]');
      if (offerBadge) {
        offerBadge.textContent = discounted ? `−${oneTime.pct}%` : '';
        offerBadge.hidden = !discounted;
      }
      const compare = this.querySelector('[data-onetime-compare]');
      if (compare) compare.hidden = !discounted;
      // Labelled "total" rather than left as a bare number. The Subscribe card
      // opposite shows a rate, so an unlabelled figure in the same position
      // invites reading this as a rate too.
      this.#secondary(
        '[data-onetime-total]',
        multi ? `${oneTime.total} ${this.dataset.lTotalSuffix}` : '',
        multi
      );
    }

    // --- quantity ladder, per purchase option --------------------------------
    // Not one ladder. Buying once uses Bundler's quantity break; subscribing
    // uses the plans' own ongoing percentages, which are different numbers.
    // Subscribe is preselected, so showing the one-time ladder would headline
    // percentages for an option the shopper is not on.
    for (const card of this.cards) {
      const badge = card.querySelector('[data-qty-badge]');
      if (!badge) continue;
      const cardQty = parseInt(card.dataset.qty, 10) || 1;

      let shown = 0;
      if (subscribing) {
        // That tier's own plan, then its recurring percentage. The recurring
        // one, not the first-order one: the badge is a property of the tier,
        // and a 50% pill on every card would say nothing about the ladder.
        const cardSet = card.dataset.plansForQty;
        const tierPlan = cardSet
          ? this.plans.find((plan) => cardSet.includes(`,${plan.id},`))
          : this.plans[0];
        const row = tierPlan ? this.#row(cardQty, tierPlan.id) : null;
        shown = row ? row.recurPct : 0;
      } else {
        // The tier percentage itself, straight from the setting that mirrors
        // Bundler — never re-derived from the floored money, which reads 14 on
        // a 15% tier.
        shown = parseInt(card.dataset.otPct, 10) || 0;
      }

      badge.textContent = `−${shown}%`;
      badge.hidden = shown <= 0;
    }

    // The ladder still owns the tier; the stepper only moves inside the one
    // tier with no upper bound, and that tier's floor moves with the mode.
    const stepper = this.querySelector('[data-qty-stepper]');
    if (stepper) {
      const floor = this.#stepperFloor(this.state.mode);
      stepper.hidden = !this.tableMode && qty < floor;
      const output = stepper.querySelector('[data-qty-output]');
      if (output) output.textContent = String(qty);
      const minus = stepper.querySelector('[data-qty-decrease]');
      const plus = stepper.querySelector('[data-qty-increase]');
      if (minus) minus.disabled = qty <= floor;
      if (plus) plus.disabled = qty >= this.maxQty;
    }

    this.#renderTierTable(qty);

    const hint = this.querySelector('[data-qty-hint]');
    if (hint) {
      const wording = subscribing ? hint.dataset.hintSubscribe : hint.dataset.hintOnetime;
      if (wording) hint.textContent = wording;
    }

    // Nothing to schedule on a one-time purchase, and nothing to show on a
    // pack size whose rule holds a single cadence: a label beside a value the
    // shopper cannot change is furniture. The cadence is then carried by the
    // recap's renewal sentence instead, so switching the recap off leaves a
    // single-cadence tier stating its price without stating how often.
    const freqRow = this.querySelector('[data-freq-row]');
    if (freqRow) freqRow.hidden = !subscribing || !this.#freqChoosable;

    this.#renderRecap(subscribing ? subscribe : oneTime, subscribing);

    if (writeToForm) this.#writeForm();
    this.#syncStickyBar(subscribing ? subscribe : oneTime, subscribing);
  }

  #renderRecap(row, subscribing) {
    const list = this.querySelector('[data-recap-list]');
    if (!list || !row) return;

    const qty = this.state.qty;
    const d = this.dataset;
    const intro = subscribing && row.hasIntro;

    // The recap describes the FIRST order, because that is what the "You pay
    // today" figure beneath it refers to. With an introductory rate the two
    // discounts are stated as separate rows rather than one blended number: a
    // shopper comparing packs needs to know which saving continues and which is
    // a one-off, and "57% off" collapsed into a single line hides exactly that.
    const rows = [
      [d.lQuantity, escapeHtml(this.#qtyLabel(qty))],
      [`${d.lPricePer} ${d.perLabel}`, escapeHtml(row.unit)]
    ];

    if (intro) {
      // PRICES here, not savings. Labelled "First order" and "Then every
      // order", a money value reads as what that order costs, so showing the
      // saving there would state a figure the shopper is not paying. The
      // percentage carries the saving.
      rows.push([d.lFirstOrder, `<b>${escapeHtml(row.total)}</b> (−${row.pct}%)`]);
      // Same "no zero row" rule as below, applied to the percentage rather
      // than to the row: a plan with no ongoing discount still has a renewal
      // price the shopper must see before buying, but "(−0%)" beside it states
      // a saving that is not there. Reachable as soon as a tier is deliberately
      // set to 0% recurring, which the 1-pack is.
      const recurPct = row.recurPct > 0 ? ` (−${row.recurPct}%)` : '';
      rows.push([d.lThenEvery, `<b>${escapeHtml(row.recurTotal)}</b>${recurPct}`]);
    } else if (row.pct > 0) {
      // No zero row. At quantity 1 there is no ladder yet, and "You save €0,00
      // (0%)" is a line that exists only to say nothing happened.
      rows.push([d.lYouSave, `<b>${escapeHtml(row.save)}</b> (${row.pct}%)`]);
    }

    const cadence = this.#cadencePhrase();
    rows.push([
      d.lDelivery,
      subscribing && cadence ? cadence.charAt(0).toUpperCase() + cadence.slice(1) : d.lOnce
    ]);

    list.innerHTML = rows
      .map(([term, value]) => `<dt>${escapeHtml(term)}</dt><dd>${value}</dd>`)
      .join('');

    this.#text('[data-recap-pay]', row.total);

    const then = this.querySelector('[data-recap-then]');
    if (then) {
      // The renewal price, its own percentage and the cadence must all be
      // visible before purchase, and doubly so when an introductory rate means
      // the first charge is not the one that repeats. Two sentences, in the
      // order the shopper reads the numbers above them: first why today's
      // figure is lower than it will be, then what actually recurs.
      //
      // No cancellation terms here: those sit under the add-to-cart button, and
      // saying them twice in one screen reads as small print rather than
      // reassurance.
      const price = intro ? row.recurTotal : row.total;
      const pct = intro ? row.recurPct : row.pct;
      const introSentence = intro ? `<b>−${row.pct}%</b> ${escapeHtml(d.lFirstOrderNote)}. ` : '';
      const recurring = `${escapeHtml(d.lThen)} <b>${escapeHtml(price)}</b> ${escapeHtml(this.#cadencePhrase())}`;
      then.innerHTML = subscribing
        ? `${introSentence}${recurring}${pct > 0 ? ` (−${pct}%)` : ''}.`
        : escapeHtml(d.onetimeNote || '');
    }
  }

  /**
   * Horizon's sticky add-to-cart bar renders the VARIANT's price. Under a
   * quantity model that is the 1-pack price no matter what the shopper picked,
   * so a shopper who scrolls past a 3-pack sees 29,99 EUR on the bar and 76,48
   * EUR in the buy box. Both figures come from the prepared rows, so this still
   * does no arithmetic; it only moves strings the server already formatted.
   */
  #syncStickyBar(row, subscribing) {
    const bar = document.querySelector('sticky-add-to-cart');
    if (!bar || !row) return;

    const label = bar.querySelector('.sticky-add-to-cart__variant');
    if (label) {
      const cadence = subscribing ? this.#cadencePhrase() : this.dataset.lOnce;
      label.textContent = cadence ? `${this.#qtyLabel(this.state.qty)} · ${cadence}` : this.#qtyLabel(this.state.qty);
      label.style.display = '';
    }

    const price = bar.querySelector('.sticky-add-to-cart__price');
    if (price) {
      let html = `<span class="price__current">${escapeHtml(row.total)}</span>`;
      // Gross — the undiscounted price at this quantity — is the only
      // defensible strikethrough reference, and it is what the saving in the
      // recap is quoted against, so the two agree.
      if (row.gross && row.gross !== row.total) {
        html += `<s class="price__compare">${escapeHtml(row.gross)}</s>`;
      }
      price.innerHTML = html;
    }
  }

  /**
   * The re-entry guard DEFERS a write rather than dropping it — on a MACROTASK,
   * and never more than one at a time.
   *
   * Both halves of that are scar tissue. Dropping (`if (this.#writing) return`)
   * silently discarded a real state change that landed in the same task as a
   * previous write, because the guard only clears on a microtask: two
   * selections in one tick left the form on the first one, buy box reading "3
   * bottles, €69,85" with quantity 1 in the form.
   *
   * The obvious fix — re-run from inside the microtask that clears the guard —
   * FROZE THE TAB on 2026-09-14. Seal's widget re-renders on every event we
   * dispatch to it and each re-render mutates the form, so the MutationObserver
   * fires on our own writes forever. A microtask chain never yields, so the
   * renderer stopped responding to input entirely rather than merely spinning.
   *
   * `setTimeout` yields to the event loop, so even a pathological oscillation
   * leaves the page usable while it settles. The real termination comes from
   * #writeFormInner reconciling instead of blindly writing: once the form
   * already says what we want, nothing is written, Seal is not told anything,
   * and the observer has nothing to react to.
   */
  #writeForm() {
    const form = this.#form();
    if (!form) return;
    if (this.#writing) {
      if (this.#writeQueued) return;
      this.#writeQueued = true;
      setTimeout(() => {
        this.#writeQueued = false;
        this.#writeForm();
      }, 0);
      return;
    }
    this.#writing = true;
    try {
      this.#writeFormInner(form);
    } finally {
      // Cleared on a microtask, after the observer has processed this batch, so
      // our own DOM writes cannot trigger another pass.
      queueMicrotask(() => {
        this.#writing = false;
      });
    }
  }

  #writeFormInner(form) {
    // The variant these prices were computed from, written over whatever
    // Horizon put in the id field.
    //
    // Horizon fills input[name="id"] from selected_or_first_available_variant.
    // On these products that is still `1-Month Subscription`, a legacy variant
    // that is on its way out — so without this the buy box would quote the base
    // variant's ladder and add a different variant to the cart. The two carry
    // the same price today, which is exactly what would make it hard to spot:
    // the total would be right and the line would be wrong.
    //
    // Harmless once the product is down to one variant, which is where this
    // design ends up.
    const variantId = this.dataset.variantId;
    const idInput = form.querySelector('input[name="id"]');
    if (idInput && variantId && String(idInput.value) !== String(variantId)) {
      idInput.value = variantId;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Horizon's real quantity input, driven rather than duplicated so its
    // quantity-rule validation still applies. The disabled hidden fallback in
    // the block markup is only enabled if that input is genuinely absent —
    // otherwise two fields named `quantity` would both submit. On
    // product.packpicker the nested quantity block is disabled, so in practice
    // the fallback is the one that carries the value.
    const real = form.querySelector('input[name="quantity"]');
    const fallback = this.querySelector('[data-qty-fallback]');
    // Every branch here compares before it writes. An unconditional write is
    // a mutation the observer sees, and an observer that fires on every pass
    // is the loop described on #writeForm.
    if (real && real !== fallback) {
      if (String(real.value) !== String(this.state.qty)) {
        real.value = String(this.state.qty);
        real.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (fallback && !fallback.disabled) fallback.disabled = true;
    } else if (fallback) {
      if (fallback.disabled) fallback.disabled = false;
      if (String(fallback.value) !== String(this.state.qty)) {
        fallback.value = String(this.state.qty);
      }
    }

    this.#writeSellingPlan(form);
  }

  /**
   * Writes the selling plan, and — more importantly — guarantees the form
   * carries exactly ONE authoritative `selling_plan` value.
   *
   * Seal Subscriptions injects its own input[name="selling_plan"] into the
   * product form. Horizon submits via new FormData(form), which collects every
   * element whose form owner is that form, including ours, associated from
   * outside via the `form` attribute. Two inputs of the same name both land in
   * the body and the last one wins. Seal's sits later in the DOM and is
   * normally empty, so a shopper who chose Subscribe got a one-time line: our
   * value was written correctly and then overridden by an empty duplicate.
   *
   * Two steps, in this order, and the order matters.
   *
   * 1. WRITE THE INPUTS DIRECTLY. Our own hidden input plus any Seal has
   *    injected all get the same value, so the form is correct on its own
   *    merits. These are native Shopify selling plans; if Seal is uninstalled
   *    or its script fails to load, add-to-cart still works. Because every
   *    input carries the SAME value, last-one-wins no longer changes the
   *    answer.
   *
   * 2. TELL SEAL, through its documented event. Writing Seal's input directly
   *    changes a value Seal does not know about, so its next init pass is free
   *    to reset it. Dispatching sealsubs:change_selected_selling_plan_id makes
   *    Seal set its own input, which leaves its state and the form agreeing and
   *    nothing to clobber. Disabling Seal's input instead also works, but
   *    leaves Seal believing the shopper chose one-time.
   */
  #writeSellingPlan(form) {
    const value = this.#currentPlanKey();
    let changed = false;

    const own = this.querySelector('[data-selling-plan-input]');
    if (own) {
      if (own.disabled) {
        own.disabled = false;
        changed = true;
      }
      if (own.value !== value) {
        own.value = value;
        changed = true;
      }
    }
    for (const el of Array.from(form.elements)) {
      if (el.name === 'selling_plan' && el !== own && el.value !== value) {
        el.value = value;
        changed = true;
      }
    }

    // Step 2 runs only on a real change. Telling Seal is what stops it
    // clobbering us, but its widget re-renders on every dispatch and each
    // re-render mutates the form, which the MutationObserver sees — so
    // dispatching unconditionally is a perpetual motion machine. Guarding on an
    // actual change makes the exchange terminate: Seal resets the input, we
    // notice the difference and re-assert, Seal re-renders, we compare, nothing
    // differs, we stop.
    if (changed) this.#setSellingPlanViaSeal(value);
  }

  /**
   * Drives Seal's own selection. An empty string selects one-time.
   *
   * The widgets are re-queried on EVERY call, never cached: Seal binds its
   * listeners to the widget elements when it creates them, so anything that
   * re-renders them rebinds to new nodes and a held reference silently stops
   * working — an intermittent bug with no error to follow. There can also be
   * more than one widget on a page, so all of them are dispatched to.
   *
   * Works with the widget hidden. `.sealsubs-target-element { display: none }`
   * in the product template's custom CSS does not disable this API, so the
   * custom UI stays the only thing the shopper sees.
   */
  #setSellingPlanViaSeal(sellingPlanId) {
    const widgets = document.querySelectorAll('.sealsubs-target-element');
    if (!widgets.length) return false;
    for (const widget of widgets) {
      widget.dispatchEvent(
        new CustomEvent('sealsubs:change_selected_selling_plan_id', {
          detail: { selling_plan_id: sellingPlanId || '' }
        })
      );
    }
    return true;
  }

  #text(selector, value) {
    const el = this.querySelector(selector);
    if (el) el.textContent = value;
  }

  #secondary(selector, value, visible) {
    const el = this.querySelector(selector);
    if (!el) return;
    el.textContent = value;
    el.hidden = !visible;
  }
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (char) => {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
  });
}

if (!customElements.get('bkg-pack-picker')) {
  customElements.define('bkg-pack-picker', BkgPackPicker);
}
